import * as React from "react"
import { cn } from "@/lib/utils"

export interface NumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  value?: number | string
  onValueChange?: (value: number | undefined) => void
}

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ className, value, onValueChange, ...props }, ref) => {
    // Internal state to manage the display string (with commas)
    const [displayValue, setDisplayValue] = React.useState("")

    // Sync internal state with external value prop
    React.useEffect(() => {
      if (value === undefined || value === "") {
        setDisplayValue("")
      } else {
        const numVal = typeof value === "string" ? parseFloat(value) : value
        if (!isNaN(numVal)) {
          setDisplayValue(numVal.toLocaleString())
        } else {
          setDisplayValue("")
        }
      }
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Remove commas to get raw digits
      const rawValue = e.target.value.replace(/,/g, "")
      
      // Allow empty input
      if (rawValue === "") {
        setDisplayValue("")
        onValueChange?.(undefined)
        return
      }

      // Check if it's a valid number format (allows one decimal point)
      if (!/^\d*\.?\d*$/.test(rawValue)) {
        return
      }

      // Handle cases like "." -> "0." or maintain as is? 
      // Standard input behavior for "." is usually "0." or just ".". 
      // Let's stick to parsing.

      // Prevent multiple leading zeros (e.g., "00") but allow "0."
      if (rawValue.length > 1 && rawValue.startsWith("0") && rawValue[1] !== ".") {
         // This block handles the "remove leading zero" requirement interactively
         // e.g. "05" -> "5"
         const fixedRaw = rawValue.replace(/^0+/, "")
         // If we stripped everything (e.g. input was "00"), default to "0" if meaningful, or allow empty?
         // If input was "05", fixedRaw is "5".
         // If input was "0", we might want to keep it "0" if it's the only digit.
         // Let's rely on standard float parsing for value, but string manipulation for display.
      }

      const numVal = parseFloat(rawValue)
      
      if (!isNaN(numVal)) {
        // Construct display value
        // We want to format the integer part with commas, but keep the decimal part as typed (to allow "1." or "1.0")
        const parts = rawValue.split(".")
        // Remove leading zeros from integer part for display, unless it's just "0"
        let integerPart = parts[0]
        if (integerPart.length > 1 && integerPart.startsWith("0")) {
          integerPart = integerPart.replace(/^0+/, "")
        }
        if (integerPart === "") integerPart = "0" // Handle ".5" -> "0.5" implicitly or keep ".5"?
        // Actually, if user types ".5", rawValue is ".5". parseFloat is 0.5.
        // Let's handle generic leading zero removal:
        // Convert integer part to number and back to string to string to strip zeros and add commas
        
        // Special handling for the "typing" experience
        let formattedDisplay = ""
        
        if (parts.length === 2) {
           // Has decimal
           const intVal = parts[0] === "" ? 0 : parseInt(parts[0], 10)
           const formattedInt = isNaN(intVal) ? "0" : intVal.toLocaleString()
           formattedDisplay = `${formattedInt}.${parts[1]}`
        } else {
           // Integer only
           const intVal = parseInt(parts[0], 10)
           formattedDisplay = isNaN(intVal) ? "" : intVal.toLocaleString()
        }

        setDisplayValue(formattedDisplay)
        onValueChange?.(numVal)
      } else {
         // Could be just "."
         if (rawValue === ".") {
             setDisplayValue("0.")
             onValueChange?.(0)
         }
      }
    }

    return (
      <input
        type="text" // Use text to allow commas
        inputMode="numeric" // Hint for mobile keyboards
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        value={displayValue}
        onChange={handleChange}
        ref={ref}
        {...props}
      />
    )
  }
)
NumberInput.displayName = "NumberInput"

export { NumberInput }
